import React from 'react';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Collapse from '@material-ui/core/Collapse';
import Button from '@material-ui/core/Button';
import WarningIcon from '@material-ui/icons/Warning';
import AnimatedProgressBar from './animatedProgressBar.js';
import { _ } from '../../functions/translator.js';
import MuiAlert from '@material-ui/lab/Alert';

class RevisionPercentageModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            expandedLevels: {},
            expandedCategories: {},
            expandedProtocols: {},
        };
    }

    componentDidMount() {
        this.initializeExpandedState();
    }

    componentDidUpdate(prevProps) {
        // Re-initialize expanded state if resultPercentages change
        if (prevProps.resultPercentages !== this.props.resultPercentages) {
            this.initializeExpandedState();
        }
    }

    initializeExpandedState = () => {
        // Auto-expand ONLY the first protocol of the first category of the first level
        if (this.props.resultPercentages && this.props.resultPercentages.length > 0) {
            const hierarchicalData = this.groupPercentagesHierarchically(this.props.resultPercentages);
            
            // Sort levels by level_id to ensure consistent ordering
            const levels = Object.values(hierarchicalData).sort((a, b) => a.level_id - b.level_id);
            const expandedLevels = {};
            const expandedCategories = {};
            const expandedProtocols = {};
            
            if (levels.length > 0) {
                const firstLevel = levels[0];
                expandedLevels[firstLevel.level_id] = true;
                
                // Sort categories by category_id to ensure consistent ordering
                const categories = Object.values(firstLevel.categories).sort((a, b) => a.category_id - b.category_id);
                if (categories.length > 0) {
                    const firstCategory = categories[0];
                    expandedCategories[`${firstLevel.level_id}_${firstCategory.category_id}`] = true;
                    
                    // Sort protocols by protocol_id to ensure consistent ordering
                    const protocols = Object.values(firstCategory.protocols).sort((a, b) => a.protocol_id - b.protocol_id);
                    if (protocols.length > 0) {
                        const firstProtocol = protocols[0];
                        expandedProtocols[`${firstLevel.level_id}_${firstCategory.category_id}_${firstProtocol.protocol_id}`] = true;
                    }
                }
            }
            
            this.setState({ 
                expandedLevels,
                expandedCategories,
                expandedProtocols
            });
        }
    }

    groupPercentagesHierarchically = (resultPercentages) => {
        const hierarchy = {};
        
        resultPercentages.forEach(percentage => {
            const levelId = percentage.level_id;
            const categoryId = percentage.card_category_id;
            const protocolId = percentage.protocol_id;
            
            // Obtener nombre del nivel
            let levelName = `Nivel ${levelId}`;
            if (this.props.projectData.levels && this.props.projectData.levels[levelId]) {
                levelName = this.props.projectData.levels[levelId].attributes.name || levelName;
            }
            
            // Obtener nombre de la categoría
            let categoryName = `Categoría ${categoryId}`;
            if (this.props.projectData.card_categories && this.props.projectData.card_categories[categoryId]) {
                categoryName = this.props.projectData.card_categories[categoryId].attributes.name || categoryName;
            }
            
            // Obtener nombre del protocolo
            let protocolName = `Protocolo ${protocolId}`;
            // Buscar en protocolgroups
            for (const protocolgroupData of Object.values(this.props.projectData.protocolgroups || {})) {
                if (protocolgroupData.protocols && protocolgroupData.protocols[protocolId]) {
                    const protocolData = protocolgroupData.protocols[protocolId];
                    const protocolgroupName = protocolgroupData.attributes.name;
                    const protocolDataName = protocolData.attributes.name;
                    protocolName = `${protocolgroupName} - ${protocolDataName}`;
                    break;
                }
            }
            
            // Crear estructura de nivel
            if (!hierarchy[levelId]) {
                hierarchy[levelId] = {
                    level_id: levelId,
                    level_name: levelName,
                    categories: {}
                };
            }
            
            // Crear estructura de categoría
            if (!hierarchy[levelId].categories[categoryId]) {
                hierarchy[levelId].categories[categoryId] = {
                    category_id: categoryId,
                    category_name: categoryName,
                    protocols: {}
                };
            }
            
            // Crear estructura de protocolo
            if (!hierarchy[levelId].categories[categoryId].protocols[protocolId]) {
                hierarchy[levelId].categories[categoryId].protocols[protocolId] = {
                    protocol_id: protocolId,
                    protocol_name: protocolName,
                    users: []
                };
            }
            
            // Agregar usuario al protocolo
            hierarchy[levelId].categories[categoryId].protocols[protocolId].users.push({
                user_name: percentage.user_name,
                prev_released_percentage: percentage.prev_released_percentage,
                post_released_percentage: percentage.post_released_percentage,
                target_percentage: percentage.target_percentage
            });
        });
        
        return hierarchy;
    }

    toggleLevel = (levelId) => {
        this.setState(prevState => ({
            expandedLevels: {
                ...prevState.expandedLevels,
                [levelId]: !prevState.expandedLevels[levelId]
            }
        }));
    }

    toggleCategory = (levelId, categoryId) => {
        const key = `${levelId}_${categoryId}`;
        this.setState(prevState => ({
            expandedCategories: {
                ...prevState.expandedCategories,
                [key]: !prevState.expandedCategories[key]
            }
        }));
    }

    toggleProtocol = (levelId, categoryId, protocolId) => {
        const key = `${levelId}_${categoryId}_${protocolId}`;
        this.setState(prevState => ({
            expandedProtocols: {
                ...prevState.expandedProtocols,
                [key]: !prevState.expandedProtocols[key]
            }
        }));
    }

    hasProtocolExceededTarget = (protocol) => {
        return protocol.users.some(user => user.post_released_percentage > user.target_percentage);
    }

    render() {
        if (!this.props.isOpen) {
            return null;
        }

        const hierarchicalData = this.props.resultPercentages ? 
            this.groupPercentagesHierarchically(this.props.resultPercentages) : {};

        // Sort levels by level_id to ensure consistent ordering
        const sortedLevels = Object.values(hierarchicalData).sort((a, b) => a.level_id - b.level_id);

        return (
            <div className="percentages-modal-overlay" onClick={this.props.onClose}>
                <div className="percentages-modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Header del Modal */}
                    <div className="percentages-modal-header">
                        <IconButton
                            aria-label="close"
                            onClick={this.props.onClose}
                            className="percentages-modal-close-btn"
                        >
                            <CloseIcon />
                        </IconButton>
                        <div className="text-center" style={{ paddingRight: '40px' }}>
                            <h4 className="percentages-modal-title">
                                {_('Actualización de los Porcentajes de Revisión')}
                            </h4>
                            <p className="percentages-modal-subtitle">
                                {_('Revisa como han cambiado los porcentajes de revisión después de la liberación')}
                            </p>
                        </div>
                    </div>
                    
                    {/* Contenido del Modal */}
                    <div className="percentages-modal-body">
                        {(this.props.showWarningMessage && this.props.warnings.revision_percentage_full_users && this.props.warnings.revision_percentage_full_users.length > 0) && 
                            <div className='w-100 px-5 py-2'>
                                <MuiAlert elevation={6} variant="filled" severity="warning">
                                    <p style={{ fontSize: '12px' }} className='text-center w-100 m-0'>
                                        {_('¡Los protocolos se han liberado correctamente! Pero, se les asigna a cada usuario los registros de forma aleatoria, proporcional a su porcentaje de revisión. Debido a esto, a los siguientes usuarios no se les asignaron todos los registros liberados:')}
                                    </p>
                                    <div className='text-center w-100 m-0 warning-alert'>
                                        {this.props.warnings.revision_percentage_full_users && this.props.warnings.revision_percentage_full_users.map((warning, idx, arr) => (
                                            <div key={idx} style={{ padding: '2px 0', fontSize: '0.9em' }}>
                                                {warning}
                                            </div>
                                        ))}
                                    </div>
                                </MuiAlert>
                            </div>
                        }
                        {sortedLevels.map((level) => (
                            <div key={level.level_id} className="hierarchy-level-card">
                                {/* Header del Nivel */}
                                <div 
                                    className="hierarchy-level-header"
                                    onClick={() => this.toggleLevel(level.level_id)}
                                >
                                    <div className="d-flex align-items-center">
                                        <div className="hierarchy-level-indicator" />
                                        <h4 className="hierarchy-level-title">
                                            {level.level_name}
                                        </h4>
                                    </div>
                                    {this.state.expandedLevels[level.level_id] ? 
                                        <ExpandLessIcon style={{ color: '#6c757d', fontSize: '24px' }} /> : 
                                        <ExpandMoreIcon style={{ color: '#6c757d', fontSize: '24px' }} />
                                    }
                                </div>

                                {/* Contenido del Nivel - Categorías */}
                                <Collapse in={this.state.expandedLevels[level.level_id]}>
                                    <div className="p-0">
                                        {Object.values(level.categories).sort((a, b) => a.category_id - b.category_id).map((category) => (
                                            <div key={category.category_id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                                                {/* Header de la Categoría */}
                                                <div 
                                                    className="hierarchy-category-header"
                                                    onClick={() => this.toggleCategory(level.level_id, category.category_id)}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div className="hierarchy-category-indicator" />
                                                        <h5 className="hierarchy-category-title">
                                                            {category.category_name}
                                                        </h5>
                                                    </div>
                                                    {this.state.expandedCategories[`${level.level_id}_${category.category_id}`] ? 
                                                        <ExpandLessIcon style={{ color: '#6c757d', fontSize: '20px' }} /> : 
                                                        <ExpandMoreIcon style={{ color: '#6c757d', fontSize: '20px' }} />
                                                    }
                                                </div>

                                                {/* Contenido de la Categoría - Protocolos */}
                                                <Collapse in={this.state.expandedCategories[`${level.level_id}_${category.category_id}`]}>
                                                    <div className="p-0">
                                                        {Object.values(category.protocols).sort((a, b) => a.protocol_id - b.protocol_id).map((protocol) => (
                                                            <div key={protocol.protocol_id} style={{ borderBottom: '1px solid #f8f9fa' }}>
                                                                {/* Header del Protocolo */}
                                                                <div 
                                                                    className="hierarchy-protocol-header"
                                                                    onClick={() => this.toggleProtocol(level.level_id, category.category_id, protocol.protocol_id)}
                                                                >
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="hierarchy-protocol-indicator" />
                                                                        <h6 className="hierarchy-protocol-title">
                                                                            {protocol.protocol_name}
                                                                        </h6>
                                                                        {this.hasProtocolExceededTarget(protocol) && (
                                                                            <WarningIcon style={{ color: '#ff9800', marginLeft: '8px', fontSize: '18px' }} />
                                                                        )}
                                                                    </div>
                                                                    {this.state.expandedProtocols[`${level.level_id}_${category.category_id}_${protocol.protocol_id}`] ? 
                                                                        <ExpandLessIcon style={{ color: '#6c757d', fontSize: '18px' }} /> : 
                                                                        <ExpandMoreIcon style={{ color: '#6c757d', fontSize: '18px' }} />
                                                                    }
                                                                </div>

                                                                {/* Contenido del Protocolo - Usuarios */}
                                                                <Collapse in={this.state.expandedProtocols[`${level.level_id}_${category.category_id}_${protocol.protocol_id}`]}>
                                                                    <div className="hierarchy-users-container">
                                                                        {protocol.users.map((user, userIndex) => (
                                                                            <AnimatedProgressBar
                                                                                key={userIndex}
                                                                                prevPercentage={user.prev_released_percentage}
                                                                                postPercentage={user.post_released_percentage}
                                                                                targetPercentage={user.target_percentage}
                                                                                user_name={user.user_name}
                                                                                protocol_name={protocol.protocol_name}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </Collapse>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Collapse>
                                            </div>
                                        ))}
                                    </div>
                                </Collapse>
                            </div>
                        ))}
                    </div>
                    
                    {/* Footer del Modal */}
                    <div className="percentages-modal-footer">
                        <Button 
                            onClick={this.props.onClose}
                            variant="contained"
                            color="primary"
                            style={{ 
                                minWidth: '120px',
                                height: '40px',
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: '500',
                                fontFamily: 'inherit',
                                color: 'white !important'
                            }}
                        >
                            {_('Cerrar')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export default RevisionPercentageModal;
